import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorBoundary from '../components/ErrorBoundary'
import SitesMap from '../components/SitesMap'
import WeatherStation from '../components/WeatherStation'
import { weatherStationForSite } from '../config/weatherStations'
import { matchesWindDirection, siteDirections, COMPASS_16 } from '../utils/windDirections'
import { useAuth } from '../contexts/AuthContext'
import Gallery, { buildGalleryImages } from '../components/Gallery'
import { SafeContent } from '../utils/htmlUtils.jsx'
import api from '../services/api'

function SitesPage() {
  const navigate = useNavigate()
  const { siteId } = useParams()
  const { isAuthenticated } = useAuth()
  
  // State management
  const [sites, setSites] = useState([])
  const [selectedSite, setSelectedSite] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    status: '',
    direction: '',
    minHeight: '',
    maxHeight: '',
    region: ''
  })
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' })
  const [showFilters, setShowFilters] = useState(false)
  // viewMode is fully determined by the presence of the :siteId route param.
  const viewMode = siteId ? 'detail' : 'list'
  const [displayMode, setDisplayMode] = useState('list') // 'list', 'map', or 'split'

  const loadSites = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const sitesData = await api.sites.getAllSites()
      setSites(sitesData)
    } catch (err) {
      setError(err.message)
      console.error('Error loading sites:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadSiteDetail = useCallback(async (id) => {
    try {
      setLoading(true)
      setError(null)
      const siteData = await api.sites.getSiteById(id)
      setSelectedSite(siteData)
    } catch (err) {
      setError(err.message)
      console.error('Error loading site detail:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  // Load sites data on mount. `loading` already starts true, so run the fetch
  // directly here rather than calling loadSites() synchronously (which would
  // set state within the effect). loadSites remains for the retry button.
  useEffect(() => {
    let ignore = false
    ;(async () => {
      try {
        const sitesData = await api.sites.getAllSites()
        if (ignore) return
        setSites(sitesData)
      } catch (err) {
        if (!ignore) {
          setError(err.message)
          console.error('Error loading sites:', err)
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    })()
    return () => { ignore = true }
  }, [])

  // Handle URL parameters for site detail view. viewMode is derived from siteId
  // during render; here we just load the detail (or clear the selection).
  useEffect(() => {
    let ignore = false
    if (!siteId) {
      // Clear any previously-selected site (deferred a microtask so this isn't
      // a synchronous setState within the effect body). In list view viewMode
      // is 'list', so selectedSite isn't rendered in the meantime.
      ;(async () => {
        await Promise.resolve()
        if (!ignore) setSelectedSite(null)
      })()
      return () => { ignore = true }
    }
    ;(async () => {
      try {
        await loadSiteDetail(siteId)
      } catch (err) {
        if (!ignore) console.error('Error loading site detail:', err)
      }
    })()
    return () => { ignore = true }
  }, [siteId, loadSiteDetail])

  // Filter and sort sites
  const filteredAndSortedSites = useMemo(() => {
    let filtered = sites.filter(site => {
      // Possible sites (not yet opened / flown) are for members only.
      if (site.possible_site && !isAuthenticated) return false

      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase()
        const matchesSearch = 
          site.name.toLowerCase().includes(search) ||
          (site.description && site.description.toLowerCase().includes(search)) ||
          (site.region && site.region.toLowerCase().includes(search))
        if (!matchesSearch) return false
      }

      // Status filter
      if (filters.status && site.status !== filters.status) return false

      // Direction filter
      if (filters.direction && !matchesWindDirection(site, filters.direction)) return false

      // Height filters
      if (filters.minHeight && site.height_meters < parseInt(filters.minHeight)) return false
      if (filters.maxHeight && site.height_meters > parseInt(filters.maxHeight)) return false

      // Region filter
      if (filters.region && site.region !== filters.region) return false

      return true
    })

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortConfig.key]
      let bValue = b[sortConfig.key]

      // Handle numeric sorting
      if (sortConfig.key === 'height_meters') {
        aValue = aValue || 0
        bValue = bValue || 0
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })

    return filtered
  }, [sites, searchTerm, filters, sortConfig, isAuthenticated])

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    return {
      statuses: [...new Set(sites.map(site => site.status).filter(Boolean))],
      // Wind directions: the 16 compass points (matched via expanded ranges),
      // not the raw free-text direction strings.
      directions: COMPASS_16,
      regions: [...new Set(sites.map(site => site.region).filter(Boolean))]
    }
  }, [sites])

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  const handleSiteClick = (site) => {
    navigate(`/sites/${site.site_id}`)
  }

  const handleMapSiteClick = (site) => {
    // For map clicks, we can either navigate to detail or show in a modal
    // For now, navigate to detail view
    navigate(`/sites/${site.site_id}`)
  }

  const handleBackToList = () => {
    navigate('/sites')
  }

  const clearFilters = () => {
    setSearchTerm('')
    setFilters({
      status: '',
      direction: '',
      minHeight: '',
      maxHeight: '',
      region: ''
    })
  }

  const formatHeight = (site) => {
    if (site.height_meters) {
      return `${site.height_meters}m`
    }
    return site.height || 'N/A'
  }

  const formatCoordinates = (lat, lng) => {
    if (!lat || !lng) return 'N/A'
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`
  }

  if (loading) {
    return (
      <div className="page-container">
        <LoadingSpinner size="large" message="Loading sites..." />
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="error-message">
          <h2>Error Loading Sites</h2>
          <p>{error}</p>
          <button onClick={loadSites} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Site detail view
  if (viewMode === 'detail' && selectedSite) {
    // Possible sites (not yet opened) are members-only.
    if (selectedSite.possible_site && !isAuthenticated) {
      return (
        <ErrorBoundary>
          <div className="page-container">
            <div className="page-header">
              <button onClick={handleBackToList} className="btn btn-secondary">
                ← Back to Sites
              </button>
              <h1>Members only</h1>
            </div>
            <div className="site-detail" style={{ padding: '32px', textAlign: 'center' }}>
              <p>
                This is a possible site that hasn't been opened for general flying yet.
                Details are only available to club members.
              </p>
              <p>
                Please <a href="/login">log in</a>, or <a href="/membership">join the club</a> to see it.
              </p>
            </div>
          </div>
        </ErrorBoundary>
      )
    }
    return (
      <ErrorBoundary>
        <div className="page-container">
          <div className="page-header">
            <button onClick={handleBackToList} className="btn btn-secondary">
              ← Back to Sites
            </button>
            <h1>{selectedSite.name}</h1>
            <p className="site-subtitle">
              {selectedSite.direction} • {formatHeight(selectedSite)} • {selectedSite.status}
            </p>
          </div>

          <div className="site-detail">
            <div className="site-detail-content">
              {/* Map Section */}
              {selectedSite.latitude && selectedSite.longitude && (
                <div className="site-section">
                  <h3>Map:</h3>
                  <div className="site-map-container">
                    <SitesMap
                      sites={[selectedSite]}
                      selectedSite={selectedSite}
                      onSiteClick={() => {}} // No action needed in detail view
                      height="500px"
                      showControls={false}
                      showMultipleLocations={true}
                    />
                  </div>
                </div>
              )}

              {/* Live weather station (sites that have one, e.g. Witchieknowe) */}
              {weatherStationForSite(selectedSite) && (
                <div className="site-section">
                  <h3>Live Weather:</h3>
                  <WeatherStation
                    stationId={weatherStationForSite(selectedSite).stationId}
                    name={weatherStationForSite(selectedSite).name}
                    mode="detailed"
                  />
                </div>
              )}

              {/* Site Status */}
              <div className="site-section">
                <h3>Site Status:</h3>
                <p className={`site-status-text ${selectedSite.status.toLowerCase()}`}>
                  {selectedSite.status === 'Open' ? 'Site is open' :
                   selectedSite.status === 'Closed' ? 'Site is closed' :
                   selectedSite.status === 'Restricted' ? 'Site is restricted' :
                   selectedSite.status}
                </p>
                {selectedSite.status === 'Open' && (
                  <p className="site-status-note">
                    Open refers to access, not conditions - a site is only flyable when the
                    weather and wind direction are right for it.
                  </p>
                )}
              </div>

              {/* More Info */}
              {selectedSite.description && (
                <div className="site-section">
                  <h3>More Info:</h3>
                  <SafeContent content={selectedSite.description} />
                </div>
              )}

              {/* Location Info */}
              <div className="site-section">
                <h3>Location Info:</h3>
                <div className="location-info-grid">
                  <div className="info-row">
                    <span className="info-label">Grid Reference:</span>
                    <span className="info-value">{selectedSite.grid_ref || formatCoordinates(selectedSite.latitude, selectedSite.longitude)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">OS Landranger:</span>
                    <span className="info-value">
                      {selectedSite.os_map ? (
                        <SafeContent content={selectedSite.os_map} preserveLinks={true} />
                      ) : 'N/A'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">What3Words:</span>
                    <span className="info-value">
                      {selectedSite.takeoff_w3w ? (
                        <a href={`https://what3words.com/${selectedSite.takeoff_w3w}`} target="_blank" rel="noopener noreferrer">
                          {selectedSite.takeoff_w3w}
                        </a>
                      ) : 'Not available'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Wind */}
              <div className="site-section">
                <h3>Wind</h3>
                <div className="wind-info">
                  {siteDirections(selectedSite).length > 0 && (
                    <div className="info-row info-row-chips">
                      <span className="info-label">Flyable in:</span>
                      <span className="direction-chips">
                        {siteDirections(selectedSite).map((d) => (
                          <span key={d} className="direction-chip-tag">{d}</span>
                        ))}
                      </span>
                    </div>
                  )}
                  {selectedSite.direction && (
                    <div className="info-row">
                      <span className="info-label">Notes:</span>
                      <span className="info-value">{selectedSite.direction}</span>
                    </div>
                  )}
                  {selectedSite.bestwinddirection && (
                    <div className="info-row">
                      <span className="info-label">Best Direction:</span>
                      <span className="info-value">{selectedSite.bestwinddirection}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Height */}
              <div className="site-section">
                <h3>Height</h3>
                <div className="height-info">
                  <div className="info-row">
                    <span className="info-label">ASL:</span>
                    <span className="info-value">{selectedSite.height || formatHeight(selectedSite)}</span>
                  </div>
                  {selectedSite.t2b && (
                    <div className="info-row">
                      <span className="info-label">Top to Bottom:</span>
                      <span className="info-value">{selectedSite.t2b}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Road Access */}
              {selectedSite.access && (
                <div className="site-section">
                  <h3>Road Access</h3>
                  <SafeContent content={selectedSite.access} />
                </div>
              )}

              {/* Parking */}
              {selectedSite.parking && (
                <div className="site-section">
                  <h3>Parking:</h3>
                  <div className="parking-info">
                    <div className="info-row">
                      <span className="info-label">Grid Reference:</span>
                      <span className="info-value">
                        {selectedSite.parkinggrid || 
                         (selectedSite.parking_latitude && selectedSite.parking_longitude ? 
                          formatCoordinates(selectedSite.parking_latitude, selectedSite.parking_longitude) : 
                          'At takeoff')}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">What3Words:</span>
                      <span className="info-value">
                        {selectedSite.parking_w3w ? (
                          <a href={`https://what3words.com/${selectedSite.parking_w3w}`} target="_blank" rel="noopener noreferrer">
                            {selectedSite.parking_w3w}
                          </a>
                        ) : 'Not available'}
                      </span>
                    </div>
                  </div>
                  <SafeContent content={selectedSite.parking} />
                </div>
              )}

              {/* Take-Off */}
              {selectedSite.takeoff && (
                <div className="site-section">
                  <h3>Take-Off:</h3>
                  <div className="takeoff-info">
                    <div className="info-row">
                      <span className="info-label">Grid Reference:</span>
                      <span className="info-value">{selectedSite.takeoffgrid || selectedSite.grid_ref || formatCoordinates(selectedSite.latitude, selectedSite.longitude)}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">What3Words:</span>
                      <span className="info-value">
                        {selectedSite.takeoff_w3w ? (
                          <a href={`https://what3words.com/${selectedSite.takeoff_w3w}`} target="_blank" rel="noopener noreferrer">
                            {selectedSite.takeoff_w3w}
                          </a>
                        ) : 'Not available'}
                      </span>
                    </div>
                  </div>
                  <SafeContent content={selectedSite.takeoff} />
                </div>
              )}

              {/* Landing */}
              {selectedSite.landing && (
                <div className="site-section">
                  <h3>Landing:</h3>
                  <div className="landing-info">
                    <div className="info-row">
                      <span className="info-label">Grid Reference:</span>
                      <span className="info-value">
                        {selectedSite.landinggrid || 
                         (selectedSite.landing_latitude && selectedSite.landing_longitude ? 
                          formatCoordinates(selectedSite.landing_latitude, selectedSite.landing_longitude) : 
                          'Various')}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">What3Words:</span>
                      <span className="info-value">
                        {selectedSite.landing_w3w ? (
                          <a href={`https://what3words.com/${selectedSite.landing_w3w}`} target="_blank" rel="noopener noreferrer">
                            {selectedSite.landing_w3w}
                          </a>
                        ) : 'Not available'}
                      </span>
                    </div>
                  </div>
                  <SafeContent content={selectedSite.landing} />
                </div>
              )}

              {/* Flying */}
              <div className="site-section">
                <h3>Flying:</h3>
                <SafeContent content={selectedSite.flying || selectedSite.flying_notes || 'General paragliding and hang gliding site. Check conditions and restrictions before flying.'} />
              </div>

              {/* Hazards */}
              {selectedSite.hazards && (
                <div className="site-section hazards-section">
                  <h3>Hazards:</h3>
                  <SafeContent content={selectedSite.hazards} />
                </div>
              )}

              {/* Notes */}
              <div className="site-section">
                <h3>Notes:</h3>
                <div className="notes-content">
                  {selectedSite.other && (
                    <div className="other-notes">
                      <SafeContent content={selectedSite.other} />
                    </div>
                  )}
                  
                  {selectedSite.lambing && (
                    <div className="restriction-note">
                      <strong>Lambing Restrictions:</strong> 
                      <SafeContent content={selectedSite.lambing_notes || 'Lambing restrictions may apply during certain periods. Please check current status before flying.'} />
                    </div>
                  )}
                  
                  {selectedSite.restricted_area && (
                    <div className="restriction-note">
                      <strong>Restricted Area:</strong>{' '}
                      Do not fly within the red boundary shown on the map above.
                    </div>
                  )}
                  
                  <p className="general-note">
                    Please check current conditions and any temporary restrictions before flying. 
                    Respect landowners, other users, and local wildlife. 
                    For questions or to report issues, please <a href="/contact">contact us</a>.
                  </p>
                </div>
              </div>

              {/* Site Gallery */}
              {selectedSite.site_id && (
                <SiteGallery siteId={selectedSite.site_id} siteName={selectedSite.name} />
              )}
            </div>
          </div>
        </div>
      </ErrorBoundary>
    )
  }

  // Sites list view
  return (
    <ErrorBoundary>
      <div className="page-container">
        <div className="page-header">
          <h1>Flying Sites Guide</h1>
          <p>Discover the best paragliding sites across the UK</p>
        </div>

        <div className="sites-controls">
          <div className="search-section">
            <input
              type="text"
              placeholder="Search sites by name, description, or region..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`btn btn-secondary ${showFilters ? 'active' : ''}`}
            >
              Filters {showFilters ? '▼' : '▶'}
            </button>
            {(searchTerm || Object.values(filters).some(f => f)) && (
              <button onClick={clearFilters} className="btn btn-outline">
                Clear All
              </button>
            )}
          </div>

          {showFilters && (
            <div className="filters-section">
              <div className="filters-grid">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="">All Statuses</option>
                  {filterOptions.statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>

                <select
                  value={filters.direction}
                  onChange={(e) => setFilters(prev => ({ ...prev, direction: e.target.value }))}
                >
                  <option value="">All Directions</option>
                  {filterOptions.directions.map(direction => (
                    <option key={direction} value={direction}>{direction}</option>
                  ))}
                </select>

                <select
                  value={filters.region}
                  onChange={(e) => setFilters(prev => ({ ...prev, region: e.target.value }))}
                >
                  <option value="">All Regions</option>
                  {filterOptions.regions.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>

                <input
                  type="number"
                  placeholder="Min height (m)"
                  value={filters.minHeight}
                  onChange={(e) => setFilters(prev => ({ ...prev, minHeight: e.target.value }))}
                />

                <input
                  type="number"
                  placeholder="Max height (m)"
                  value={filters.maxHeight}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxHeight: e.target.value }))}
                />
              </div>
            </div>
          )}

          <div className="results-info">
            Showing {filteredAndSortedSites.length} of {sites.length} sites
          </div>
        </div>

        {/* View Toggle */}
        <div className="view-toggle">
          <button 
            className={`view-toggle-button ${displayMode === 'list' ? 'active' : ''}`}
            onClick={() => setDisplayMode('list')}
          >
            📋 List View
          </button>
          <button 
            className={`view-toggle-button ${displayMode === 'map' ? 'active' : ''}`}
            onClick={() => setDisplayMode('map')}
          >
            🗺️ Map View
          </button>
          <button 
            className={`view-toggle-button ${displayMode === 'split' ? 'active' : ''}`}
            onClick={() => setDisplayMode('split')}
          >
            📊 Split View
          </button>
        </div>

        {/* Content based on display mode */}
        <div className={`sites-content ${displayMode}-view`}>
          {/* Map Section */}
          {(displayMode === 'map' || displayMode === 'split') && (
            <div className="map-section">
              <SitesMap
                sites={filteredAndSortedSites}
                onSiteClick={handleMapSiteClick}
                height={displayMode === 'map' ? '600px' : '400px'}
                showControls={true}
              />
            </div>
          )}

          {/* List Section */}
          {(displayMode === 'list' || displayMode === 'split') && (
            <div className="list-section">
              <div className="sites-table-container">
                <table className="sites-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')} className="sortable">
                        Name {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('direction')} className="sortable">
                        Direction {sortConfig.key === 'direction' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('height_meters')} className="sortable">
                        Height {sortConfig.key === 'height_meters' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('region')} className="sortable">
                        Region {sortConfig.key === 'region' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('status')} className="sortable">
                        Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>Coordinates</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedSites.map(site => (
                      <tr key={site.site_id} className="site-row">
                        <td className="site-name" data-label="Name">
                          <strong>{site.name}</strong>
                          {site.lambing && <span className="restriction-badge">🐑</span>}
                          {site.restricted_area && <span className="restriction-badge">🚫</span>}
                        </td>
                        <td data-label="Direction">{site.direction}</td>
                        <td data-label="Height">{formatHeight(site)}</td>
                        <td data-label="Region">{site.region || 'N/A'}</td>
                        <td data-label="Status">
                          <span className={`status ${site.status.toLowerCase()}`}>
                            {site.status}
                          </span>
                        </td>
                        <td className="coordinates" data-label="Coordinates">
                          {formatCoordinates(site.latitude, site.longitude)}
                        </td>
                        <td data-label="">
                          <button 
                            onClick={() => handleSiteClick(site)}
                            className="btn btn-sm btn-primary"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredAndSortedSites.length === 0 && (
                  <div className="no-results">
                    <p>No sites found matching your criteria.</p>
                    <button onClick={clearFilters} className="btn btn-primary">
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  )
}

/**
 * Site Gallery sub-component
 * Maps site IDs to their image gallery folders and renders Gallery component.
 * The Joomla site used folder names that mostly match site IDs with some variations.
 */
const SITE_GALLERY_MAP = {
  'cacra': 'cacrabank',
  'minto': 'minto_hills',
  'newark': null, // No gallery folder
  'witchieknowe': 'witchieknowe',
  'fastheugh': 'fastheugh',
  'bridgend': 'bridgend',
  'bodesbeck': 'bodesbeck',
  'mountbenger': 'mountbenger',
  'foulshiels': 'foulshiels',
  'jubileecairn': 'jubileecairn',
  'craighill': 'craighill',
  'ettrickpen': 'ettrickpen',
  'sundhope': 'sundhope',
}

function SiteGallery({ siteId, siteName }) {
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Map site ID to gallery folder
    const folder = SITE_GALLERY_MAP[siteId] || siteId
    // No folder -> nothing to fetch. `loading` stays true, so the component
    // renders null (same as the previous loading=false + empty-images path).
    if (!folder) return

    let cancelled = false
    // For now, we'll try to load a manifest file from S3
    // In production, this would be generated during the sync process
    // Fallback: the Gallery component handles empty image arrays gracefully
    const MEDIA_URL = import.meta.env.VITE_MEDIA_URL || 'https://wingbeat-dev-media.s3.eu-west-2.amazonaws.com'
    
    fetch(`${MEDIA_URL}/images/${folder}/manifest.json`)
      .then(r => r.ok ? r.json() : [])
      .then(fileList => {
        if (cancelled) return
        if (Array.isArray(fileList) && fileList.length > 0) {
          setImages(buildGalleryImages(folder, fileList))
        }
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [siteId])

  if (loading || images.length === 0) return null

  return (
    <div className="site-section">
      <h3>Gallery</h3>
      <Gallery
        images={images}
        title={`Photos from ${siteName}`}
        columns={4}
      />
    </div>
  )
}

export default SitesPage